import { useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportEditor } from "@/routes/incidents/ReportEditor";
import { useCurrentUser } from "@/api/queries/me";
import { jurisdictionLine } from "@/lib/jurisdiction";
import { isDemonstrationTenant } from "@/lib/demonstration";
import { ExposureTable, type ExposureRow } from "@/components/exposure-table";
import { useIncidentReport } from "@/api/queries/incidents";
import { useIncidentTypedReport } from "@/api/queries/reports";
import type { components } from "@/api/types";
import { formatDateTime } from "@/lib/format";
import { DEFAULT_DEPT_NAME, printReport } from "@/lib/print-report";
import {
  findSection,
  reportTypeLabel,
  sectionRows,
  SELECTABLE_REPORT_TYPES,
  type ReportType,
} from "@/lib/reports";
import { useDownloadReportPdf } from "@/routes/incidents/useDownloadReportPdf";

type GeneratedReport = components["schemas"]["GeneratedReportRead"];

export function TypedReportView({
  incidentId,
  incidentNumber,
  tz,
}: {
  incidentId: string;
  /** The department's own incident number, for the printed letterhead. The
   *  typed-report payload carries one only on incident_summary reports, and a
   *  raw UUID on a filed record is worse than no number at all. */
  incidentNumber?: string | null;
  tz?: string;
}) {
  const [type, setType] = useState<ReportType>("incident_summary");
  const { data, isLoading, isError } = useIncidentTypedReport(incidentId, type);
  const draft = useIncidentReport(incidentId);
  const pdf = useDownloadReportPdf(incidentId);
  const me = useCurrentUser();
  // Same fallback the filename uses — printed letterhead and saved filename
  // must never name two different departments.
  const deptName = me.data?.department?.name ?? DEFAULT_DEPT_NAME;
  // Same gate the PDF uses. A sandbox report printed from the browser must say
  // it is synthetic on the page, or it is indistinguishable from a filed record.
  const isDemonstration = isDemonstrationTenant(me.data?.department);

  const reportTitle = data?.title ?? "Incident report";
  // Hoisted: the letterhead and the closing disclosure must name the SAME
  // incident, and computing it twice is how they come to disagree.
  const reportNumber =
    incidentNumber ??
    (findSection(data?.sections ?? [], "Incident")?.incident_number as
      | string
      | undefined) ??
    null;

  return (
    <div className="report-doc space-y-4">
      <ReportLetterhead
        deptName={deptName}
        jurisdiction={me.data?.department?.jurisdiction}
        crestUrl={me.data?.department?.logo_url}
        isDemonstration={isDemonstration}
        title={reportTitle}
        tz={tz}
        reportNumber={reportNumber}
        isAuthoritative={data?.is_authoritative ?? false}
      />
      <div className="flex flex-wrap gap-2 print:hidden" role="tablist">
        {SELECTABLE_REPORT_TYPES.map((t) => (
          <Button
            key={t.value}
            size="sm"
            variant={t.value === type ? "default" : "outline"}
            aria-selected={t.value === type}
            role="tab"
            data-testid={`report-type-${t.value}`}
            onClick={() => setType(t.value)}
          >
            {t.label}
          </Button>
        ))}
        {draft.isPending ? (
          // "Still loading" must not fall through to the print branch: against
          // a live API the typed report can resolve before the draft, and the
          // enabled print fallback would hand over exactly the browser-print
          // document this component exists to stop serving. `isPending`, not
          // `isError` — the Reports page reaches this view after a 404 and
          // legitimately wants the print fallback.
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            data-testid="report-pdf-pending"
            disabled
          >
            Download PDF
          </Button>
        ) : draft.data?.pdf_s3_key ? (
          // A worker-rendered PDF exists, so Download PDF must hand over THAT
          // file — the filed record — never a browser print of the on-screen
          // DOM. Same mechanism as ReportEditor's row button, via the shared
          // hook. Exactly one affordance per state, never two competing
          // downloads.
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            data-testid="download-report-pdf-header"
            disabled={pdf.isPending}
            onClick={pdf.download}
          >
            {pdf.isPending ? "Preparing…" : "Download PDF"}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            data-testid="print-report-pdf"
            disabled={!data}
            onClick={() =>
              printReport(deptName, data?.title ?? "Incident report", tz)
            }
          >
            Download PDF
          </Button>
        )}
      </div>

      {pdf.isError ? (
        <p className="text-sm text-destructive" data-testid="report-pdf-error">
          Could not download the report PDF. Try again, or re-open the page.
        </p>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : isError || !data ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Could not load the {reportTypeLabel(type)} report.
          </CardContent>
        </Card>
      ) : (
        <>
          <ReportBody report={data} tz={tz} incidentId={incidentId} />
          <ReportClosing
            tz={tz}
            isDemonstration={isDemonstration}
            deptName={deptName}
            reportNumber={reportNumber}
          />
        </>
      )}
    </div>
  );
}

/**
 * The printed letterhead.
 *
 * Print-only: on screen the department name already sits in the topbar and the
 * report title in the card header, but on paper the page must identify itself
 * — the PDF leaves the app and is read as a standalone record, often years
 * later, by someone who has never seen the app.
 *
 * The letterhead names the TENANT DEPARTMENT, exactly as a department's own
 * filed record would (and exactly as `printReport` names the saved file).
 * RespAI appears only as generated-by provenance, in the page footer and the
 * closing block. See docs/design/report-print-design-language.md §2.
 */
function ReportLetterhead({
  deptName,
  jurisdiction,
  crestUrl,
  isDemonstration,
  title,
  reportNumber,
  isAuthoritative,
  tz,
}: {
  deptName: string;
  jurisdiction?: string | null;
  crestUrl?: string | null;
  isDemonstration: boolean;
  title: string;
  reportNumber: string | null;
  isAuthoritative: boolean;
  tz?: string;
}) {
  return (
    <>
      <header className="letterhead hidden print:table">
        <div className="letterhead__identity">
          {/* The crest renders from the department's own logo (RES-159), the
              same one the topbar shows. Absent rather than empty — the
              name-only lockup is complete on its own. */}
          {crestUrl ? (
            <div className="letterhead__crest">
              <img src={crestUrl} alt="" />
            </div>
          ) : null}
          <div>
            <p className="letterhead__dept">{deptName}</p>
            <p className="letterhead__juris">{jurisdictionLine(jurisdiction)}</p>
          </div>
        </div>
        <div className="letterhead__meta">
          <p className="letterhead__title">{title}</p>
          {reportNumber ? (
            <p className="letterhead__number">{reportNumber}</p>
          ) : null}
          <p className="letterhead__generated">
            Generated {generatedStamp(tz)}
          </p>
          <p className="mt-1">
            {isAuthoritative ? (
              <span className="pill pill--ok">Authoritative</span>
            ) : (
              <span className="pill pill--warn">Draft — unverified</span>
            )}
          </p>
        </div>
      </header>
      <div className="letterhead-rule hidden print:block" />
      {isDemonstration ? (
        <div className="demo-banner hidden print:block">
          <strong>Demonstration report.</strong> Synthetic incident and exposure
          data prepared for product evaluation. This is not a record of an
          actual {deptName} response and must not be filed as one.
        </div>
      ) : null}
    </>
  );
}

/**
 * Provenance, sign-off and record status — the block that turns a printout
 * into a record. Print-only: on screen none of it means anything, and the
 * three travel together so a signature page never lands alone.
 */
function ReportClosing({
  tz,
  isDemonstration,
  deptName,
  reportNumber,
}: {
  tz?: string;
  isDemonstration: boolean;
  deptName: string;
  reportNumber: string | null;
}) {
  return (
    <div className="closing hidden print:block">
      <div className="section-head">
        <h4 className="section-head__title">Provenance &amp; Sign-off</h4>
        <span className="section-head__scope">
          How this document was produced, and by whose authority it is filed
        </span>
      </div>
      <div className="provenance">
        <table>
          <tbody>
            <tr>
              <td className="label">Generated</td>
              <td className="value">{generatedStamp(tz)}</td>
            </tr>
            <tr>
              <td className="label">Generated by</td>
              <td className="value">
                <img
                  className="provenance__mark"
                  src="/respai-full-logo.svg"
                  alt="RespAI"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Six fields in two rows: a name-and-rank line separate from the
          signature is what makes a signature attributable later, and the
          corrective-actions field gives a review that found something
          somewhere to put it. */}
      <div className="signoff">
        <div className="signoff__field">
          <div className="signoff__line" />
          <p className="signoff__label">Incident Commander — name and rank</p>
        </div>
        <div className="signoff__field">
          <div className="signoff__line" />
          <p className="signoff__label">Health &amp; safety officer</p>
        </div>
        <div className="signoff__field">
          <div className="signoff__line" />
          <p className="signoff__label">Date reviewed</p>
        </div>
      </div>
      <div className="signoff">
        <div className="signoff__field">
          <div className="signoff__line" />
          <p className="signoff__label">Signature</p>
        </div>
        <div className="signoff__field">
          <div className="signoff__line" />
          <p className="signoff__label">Signature</p>
        </div>
        <div className="signoff__field">
          <div className="signoff__line" />
          <p className="signoff__label">
            Corrective actions assigned to / due
          </p>
        </div>
      </div>
      <div className="record-status">
        <p>
          <strong>Exposure disclosure.</strong> Figures are aggregated from this
          incident only — they are not a complete 8-hour TLV-TWA, and they do
          not account for exposure outside this response. Total VOC is an
          unspeciated aromatic load measured by photoionisation; no
          benzene-specific measurement is claimed.
        </p>
        <p>
          <strong>Record status.</strong> Machine-assisted draft requiring
          officer review; not an authoritative record until signed. Source data:
          sensor readings and the incident commander's voice record, held in the
          RespAI platform. The raw transcript is not reproduced here.
        </p>
        {isDemonstration ? (
          <p>
            <strong>Demonstration data:</strong> {deptName}
            {reportNumber ? ` · ${reportNumber}` : ""} — synthetic evaluation
            content, not a record of an actual response.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Every timestamp on the page is a 24-hour clock in the department's zone
 *  (RES-164). The generated stamp is no exception — a JS locale string next to
 *  "2026-05-18 07:35 NDT" reads as two documents spliced together. */
function generatedStamp(tz?: string): string {
  return formatDateTime(new Date().toISOString(), tz);
}

/** Section heading. On screen this is the small bold label the report has
 *  always had; on paper it becomes the tracked rule in the accent colour, with
 *  the scope caption that tells a reader what the section covers before they
 *  read a single row. */
function SectionHead({ title, scope }: { title: string; scope: string }) {
  return (
    <div className="section-head mb-2 flex items-baseline justify-between">
      <h4 className="section-head__title text-sm font-semibold">{title}</h4>
      <span className="section-head__scope hidden print:block">{scope}</span>
    </div>
  );
}

function ReportBody({
  report,
  tz,
  incidentId,
}: {
  report: GeneratedReport;
  tz?: string;
  incidentId: string;
}) {
  return (
    // report-card: the print stylesheet strips the card's border, padding and
    // surface. Card chrome is screen furniture — on paper it reads as a
    // screenshot of an app rather than as a document.
    <Card className="report-card">
      {/* The printed letterhead already carries the title and the
          authoritative/draft status, so the card header is screen-only. */}
      <CardHeader className="space-y-3 print:hidden">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{report.title}</CardTitle>
          {report.is_authoritative ? (
            <Badge variant="success">Authoritative</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-6 print:p-0">
        {report.report_type === "incident_summary" ? (
          <IncidentSummarySections
            sections={report.sections}
            tz={tz}
            incidentId={incidentId}
          />
        ) : report.report_type === "exposure" ? (
          <ExposureSections sections={report.sections} />
        ) : report.report_type === "voice_record" ? (
          <VoiceRecordSections sections={report.sections} tz={tz} />
        ) : (
          <NarrativeSections
            sections={report.sections}
            tz={tz}
            incidentId={incidentId}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** One cell of the facts grid. The `facts__*` classes are print-only (they
 *  turn the grid into labelled cells opened by a left hairline, small caps
 *  over a bold value); the Tailwind classes are what the screen sees. */
function Field({
  label,
  value,
  numeric = false,
}: {
  label: string;
  value: unknown;
  /** Numerics and timestamps print in JetBrains Mono with tabular figures. */
  numeric?: boolean;
}) {
  return (
    <div className="facts__cell">
      <dt className="facts__label text-xs font-medium text-muted-foreground">
        {label}
      </dt>
      <dd className={`facts__value text-sm${numeric ? " num" : ""}`}>
        {value == null || value === "" ? "—" : String(value)}
      </dd>
    </div>
  );
}

function IncidentSummarySections({
  sections,
  tz,
  incidentId,
}: {
  sections: GeneratedReport["sections"];
  tz?: string;
  incidentId: string;
}) {
  const head = findSection(sections, "Incident");
  const events = sectionRows<Record<string, unknown>>(
    findSection(sections, "Timeline"),
    "events",
  );

  // The IncidentEvent table is usually empty for a quick burn-test incident, so
  // the report's own "Timeline" section comes back with no rows. Synthesize the
  // dispatch/clear milestones from the incident header — mirroring how the
  // incident-detail page builds its timeline — so the panel always reflects the
  // real recorded times instead of a bare "No events recorded".
  const dispatchedAt = head?.dispatched_at as string | null | undefined;
  const clearedAt = head?.cleared_at as string | null | undefined;
  const timeline: { ts: string; label: string; sub?: string | null }[] = [];
  if (dispatchedAt) timeline.push({ ts: dispatchedAt, label: "Dispatched" });
  for (const e of events) {
    const ts = e.occurred_at as string | null;
    if (!ts) continue;
    timeline.push({
      ts,
      label: String(e.event_type ?? "Event"),
      sub: e.task ? String(e.task) : null,
    });
  }
  if (clearedAt) {
    timeline.push({
      ts: clearedAt,
      label: head?.status === "finalized" ? "Finalized" : "Cleared",
    });
  }
  timeline.sort((a, b) => a.ts.localeCompare(b.ts));

  return (
    <div className="space-y-6">
      <SectionHead title="Incident" scope="As recorded at dispatch and on scene" />
      <dl className="facts grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field
          label="Incident #"
          value={head?.incident_number ?? incidentId}
          numeric
        />
        <Field label="Type" value={head?.incident_type} />
        <Field label="Status" value={head?.status} />
        <Field
          label="Dispatched"
          value={formatDateTime(head?.dispatched_at as string | null, tz)}
          numeric
        />
        <Field
          label="Cleared"
          value={formatDateTime(head?.cleared_at as string | null, tz)}
          numeric
        />
        <Field label="Address" value={head?.address} />
      </dl>
      <div>
        <SectionHead
          title="Timeline"
          scope="Dispatch, on-scene milestones and clear"
        />
        {timeline.length > 0 ? (
          <ol className="space-y-2 border-l border-border pl-4">
            {timeline.map((t, idx) => (
              <li key={idx}>
                <div className="num text-xs text-muted-foreground">
                  {formatDateTime(t.ts, tz)}
                </div>
                <div className="text-sm font-medium">
                  {t.label}
                  {t.sub ? (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {t.sub}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No events recorded.</p>
        )}
      </div>
    </div>
  );
}

function ExposureSections({
  sections,
}: {
  sections: GeneratedReport["sections"];
}) {
  const rows = sectionRows<Record<string, unknown>>(
    findSection(sections, "Per-firefighter exposure"),
    "firefighters",
  );
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No exposure records captured for this incident.
      </p>
    );
  }
  // Same component, same status logic, same numbers as the Exposure tab. The
  // API now emits the full record (dose, TWA, confidence) under these exact
  // field names, so no per-surface mapping stands between the two.
  // The payload now carries user_name/user_badge_number under the same field
  // contract as /v1/exposure-records, so the table needs no side-channel map —
  // one dataset, one identity path, in the tab and in the printed PDF alike.
  const records = rows as unknown as ExposureRow[];
  return (
    <div>
      <SectionHead
        title="Per-firefighter exposure"
        scope="Per firefighter · sensor-measured channels, peak and cumulative dose"
      />
      <ExposureTable records={records} />
    </div>
  );
}

function VoiceRecordSections({
  sections,
  tz,
}: {
  sections: GeneratedReport["sections"];
  tz?: string;
}) {
  const segments = sectionRows<Record<string, unknown>>(
    findSection(sections, "Audio segments"),
    "segments",
  );
  if (segments.length === 0) {
    return <p className="text-sm text-muted-foreground">No audio recorded.</p>;
  }
  return (
    <div className="space-y-3">
      <SectionHead
        title="Audio segments"
        scope="From the IC voice record · transcript status per segment"
      />
      {segments.map((s, idx) => (
        <div key={idx} className="rounded-md border border-border p-3 print:break-inside-avoid">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="num">
              {formatDateTime(s.started_at as string | null, tz)}
            </span>
            <Badge
              variant={s.transcript ? "secondary" : "outline"}
              className="text-[10px]"
            >
              {String(s.transcript_status ?? "unknown")}
            </Badge>
          </div>
          {s.transcript ? (
            <pre className="mt-2 whitespace-pre-wrap text-sm">
              {String(s.transcript)}
            </pre>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Not transcribed.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function NarrativeSections({
  sections,
  tz,
  incidentId,
}: {
  sections: GeneratedReport["sections"];
  tz?: string;
  incidentId: string;
}) {
  const narrative = findSection(sections, "Narrative");
  const draft = narrative?.draft as Record<string, unknown> | null | undefined;
  return (
    <div className="space-y-4">
      <SectionHead
        title="Narrative"
        scope="After-action draft · reviewed status as of generation"
      />
      <dl className="facts grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Draft status" value={narrative?.status} />
        <Field
          label="Reviewed at"
          numeric={Boolean(narrative?.reviewed_at)}
          value={
            narrative?.reviewed_at
              ? formatDateTime(narrative.reviewed_at as string, tz)
              : "Not reviewed"
          }
        />
      </dl>
      {draft ? (
        <NarrativePreview draft={draft} tz={tz} />
      ) : (
        <p className="text-sm text-muted-foreground">
          No narrative draft generated yet.
        </p>
      )}
      {/* print:hidden — the saved PDF is the REPORT, not the workbench that
          produced it. Without this the chief's document ends with the edit
          textareas, the Save draft / Finalize buttons and a "Saved ✓"
          timestamp printed into it. */}
      <div className="border-t border-border pt-4 print:hidden">
        <h4 className="mb-2 text-sm font-semibold">Edit &amp; finalize draft</h4>
        <ReportEditor incidentId={incidentId} />
      </div>
    </div>
  );
}

// Render the after-action draft as a readable report preview rather than a raw
// JSON dump. The draft body is freeform (the editor guarantees the four core
// fields), so each section coerces defensively and falls back to an em-dash
// when a field is absent. Richer drafts (command decisions, resources, on-scene
// times) surface only when those keys are actually present.
function NarrativePreview({
  draft,
  tz,
}: {
  draft: Record<string, unknown>;
  tz?: string;
}) {
  const hazards = asStrings(draft.hazards_identified);
  const decon = asStrings(draft.decon_actions);
  const resources = asStrings(draft.resources_requested);
  const gaps = asStrings(draft.gaps);
  const decisions = asRows(draft.ic_decisions);
  const notes =
    typeof draft.after_action_notes === "string"
      ? draft.after_action_notes.trim()
      : "";
  const commenced = (draft.commencement_time ?? draft.commenced_at) as
    | string
    | null
    | undefined;
  const cleared = (draft.clear_time ?? draft.cleared_at) as
    | string
    | null
    | undefined;

  return (
    <div className="space-y-5 rounded-md border border-border bg-card p-4 print:border-0 print:p-0">
      {commenced || cleared ? (
        <dl className="facts grid grid-cols-2 gap-3 sm:grid-cols-3">
          {commenced ? (
            <Field
              label="Commenced"
              value={formatDateTime(commenced, tz)}
              numeric
            />
          ) : null}
          {cleared ? (
            <Field label="Cleared" value={formatDateTime(cleared, tz)} numeric />
          ) : null}
        </dl>
      ) : null}

      <PreviewSection title="Hazards identified">
        <BulletList items={hazards} />
      </PreviewSection>

      <PreviewSection title="Decontamination actions">
        <BulletList items={decon} />
      </PreviewSection>

      {decisions.length > 0 ? (
        <PreviewSection title="Command decisions">
          <ul className="space-y-2">
            {decisions.map((d, idx) => (
              <li key={idx} className="text-sm">
                <div className="font-medium">
                  {d.decision ? String(d.decision) : "Decision"}
                  {d.time ? (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {formatDateTime(String(d.time), tz)}
                    </span>
                  ) : null}
                </div>
                {d.rationale ? (
                  <div className="text-muted-foreground">
                    {String(d.rationale)}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </PreviewSection>
      ) : null}

      {resources.length > 0 ? (
        <PreviewSection title="Resources requested">
          <BulletList items={resources} />
        </PreviewSection>
      ) : null}

      <PreviewSection title="After-action notes">
        {notes ? (
          <p className="whitespace-pre-wrap text-sm">{notes}</p>
        ) : (
          <Empty />
        )}
      </PreviewSection>

      {/* Open items are a callout on paper: the one block on the page a reader
          must act on before filing the record. */}
      <PreviewSection title="Open items to verify" className="callout">
        <BulletList items={gaps} />
      </PreviewSection>
    </div>
  );
}

/** `className` carries print-only vocabulary from the shared design language
 *  (e.g. `callout`). Those class names have no styles outside `@media print`,
 *  so they change nothing on screen. */
function PreviewSection({
  title,
  children,
  className = "",
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`space-y-1 ${className}`.trimEnd()}>
      <h5 className="text-sm font-semibold">{title}</h5>
      {children}
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) return <Empty />;
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm">
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
}

function Empty() {
  return <p className="text-sm text-muted-foreground">—</p>;
}

function asStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(asItemText).filter(Boolean);
}

// Draft bodies are freeform JSON: list items are usually strings, but richer
// drafts carry objects (e.g. resources_requested holding {type, count}).
// String() on those rendered literal "[object Object]" bullets in the report
// preview (2026-07-30). Pick the readable keys; fall back to compact JSON so
// nothing ever renders opaquely.
function asItemText(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (v == null) return "";
  if (typeof v === "object" && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    const label = [o.label, o.name, o.type, o.unit, o.resource, o.description].find(
      (x): x is string => typeof x === "string" && x.trim() !== "",
    );
    if (label) {
      const count =
        typeof o.count === "number" && o.count > 1 ? ` ×${o.count}` : "";
      const status =
        typeof o.status === "string" && o.status.trim() ? ` — ${o.status}` : "";
      return `${label}${count}${status}`;
    }
    return JSON.stringify(o);
  }
  return String(v);
}

function asRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (v): v is Record<string, unknown> =>
      typeof v === "object" && v !== null && !Array.isArray(v),
  );
}


